/* eslint-disable no-invalid-this */
/* eslint-disable max-len */

import ApiService, { ACCOUNTS_API_CONTEXT, ACCOUNTS_API_HOST } from './ApiService';
import { download } from './helpers';

/**
 * @override
 */
class OperationsService extends ApiService {
    /**
     * constructor
     */
    constructor() {
        super({ baseURL: ACCOUNTS_API_HOST });
    }
    /**
     * Get operations list
     * @param {Object} sort Sort
     * @param {Object} config Axios config
     * @return {Object} Response with list of operations
     */
    getOperations = async (sort, config) => {
        if (sort.trim() !== '') {
            return this.getList(`${ACCOUNTS_API_CONTEXT}/operations?$orderby=${sort}`, config);
        } else {
            const operationsList = await this.getList(`${ACCOUNTS_API_CONTEXT}/operations`, config);
            return operationsList;
        }
    };
    /**
     * Get operation
     * @param {String} uuid Account UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation
     */
    getOperationsByUUId = (uuid, config) => {
        return this.get(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}`, config);
    };
    /**
     * Get operation
     * @param {String} uuid Account UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation
     */
    getOperationSuboperations = (uuid, config) => {
        return this.get(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/operations`, config);
    };
    /**
     * @param {String} url Url
     * @param {Object} config Axios config
     * @return {Object} Response with list of operations
     */
    getPagination = (url, config) => this.get(`${url}`, config);
    /**
     * Buscar operaciones
     * @param {Array} search Búsqueda
     * @param {Object} config Axios config
     * @param {Object} sort Sort
     * @return {Object} Response with accounts
     */
    getSearch = (search, config, sort = false) => {
        const sortQueryParam = sort ? `&$orderby=${sort}` : '';
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations?$search=${search}${sortQueryParam}`, config);
    };
    /**
     * Get operations Documents
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation documents data
     */
    getOperationDocuments = (uuid, config) => {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/documents`, config);
    };

    /**
     * @override
     */
    getDocument = (uuid, uuidDocument) => {
        return this.get(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/documents/${uuidDocument}`, {
            responseType: 'blob'
        }).then(res => res.data);
    };
    /**
     * Get operations historic
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation historic data
     */
    getOperationHistoric = (uuid, config) => {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/historical`, config);
    };
    /**
     * Get operation interveners
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation intervenes data
     */
    getOperationInterveners = (uuid, config) => {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/interveners`, config);
    };
    /**
     * Get operation Issues
     * @param {String} uuid operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation issues data
     */
    getOperationIssues = (uuid, config) => {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/issues`, config);
    };
    /**
     * Get operation Types list
     * @param {Object} sort Sort
     * @param {Object} config Axios config
     * @return {Object} Response with operation issues data
     */
    getOperationTypes = (sort = '', config = {}) => {
        if (sort.trim() !== '') {
            return this.getList(`${ACCOUNTS_API_CONTEXT}/operation-type?$orderby=${sort}`, config);
        } else {
            return this.getList(`${ACCOUNTS_API_CONTEXT}/operation-type`, config);
        }
    };
    /**
     * Buscar tipo de operacion
     * @param {Array} search Búsqueda
     * @param {Object} config Axios config
     * @return {Object} Response with opertaion-types
     */
    getSearchOperationType = (search, config) =>
        this.getList(`${ACCOUNTS_API_CONTEXT}/operation-type?$search=${search}`, config);

    /**
     * Create an operation
     * @param {Object} body body of request
     * @return {Object} Response with opertaion-types
     */
    createOperation = body => this.post(`${ACCOUNTS_API_CONTEXT}/operations`, body);

    /**
     * Update operation
     * @param {String} uuid UUID of operation
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    updateOperation = (uuid, payload) => {
        return this.patch(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}`, payload);
    };

    /**
     * Delete operation
     * @param {String} uuid Operation id
     * @return {Object} Response of delete operation
     */
    deleteByUUIId = uuid => {
        return this.delete(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}`, {});
    };

    /**
     * @override
     */
    download = (sort, config, headers) => {
        if (sort.trim() !== '') {
            return this.getDownload(`${ACCOUNTS_API_CONTEXT}/operations/download?$orderby=${sort}`, config, {
                headers
            });
        } else {
            return this.getDownload(`${ACCOUNTS_API_CONTEXT}/operations/download`, config, { headers });
        }
    };

    /**
     * Get account Operations
     * @param {String} uuid Account UUID
     * @param {Object} config Axios config
     * @return {Object} Response with account operations data
     */
    getRelatedOperations = (uuid, config) => {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/accounts/${uuid}/operations`, config);
    };

    /**
     * Change operation status
     * @param {String} uuid Operation UUID
     * @param {Object} body body of request
     * @return {Object} Response with opertaion changed
     */
    changeOperationStatus = (uuid, body) => {
        return this.post(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/status-change`, body);
    };

    /**
     * Get intervener fatca data
     * @param {String} uuid_operation Operation UUID
     * @param {String} uuidDocument Document UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener legal data
     */
    removeDocument = (uuid_operation, uuidDocument, config) => {
        return this.delete(`${ACCOUNTS_API_CONTEXT}/operations/${uuid_operation}/documents/${uuidDocument}`, config);
    };

    /**
     * @override
     */
    upload = payload => {
        const config = {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        };

        const formData = new FormData();
        for (let i = 0; i < payload.files.length; i++) {
            formData.append('attachment', payload.files[i]);
            formData.append('filename', payload.filesName[i]);
            formData.append('category', typeof payload.category === 'string' ? payload.category : payload.category[i]);
            formData.append(
                'subcategory',
                typeof payload.subcategory === 'string' ? payload.subcategory : payload.subcategory[i]
            );
            formData.append('validated', payload.validated);
        }
        return this.post(`${ACCOUNTS_API_CONTEXT}/operations/${payload.uuid}/documents`, formData, config);
    };

    /**
     * Get intervener Documents
     * @param {String} uuid Operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with intervener documents data
     */
    getDocumentsValidations = (uuid, config) => {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/document-validations`, config);
    };

    /**
     * Change document objects
     * @param {String} uuid UUID account
     * @param {String} uuidDocuemnt UUID document
     * @param {Object} payload modified properties
     * @return {Object} Response data
     */
    updateDocument = (uuid, uuidDocuemnt, payload) => {
        return this.patch(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/documents/${uuidDocuemnt}`, payload);
    };

    /**
     * Send operation to RD
     * @param {String} uuid UUID of operation
     * @return {Object} Response data
     */
    sendToRD = uuid => {
        return this.post(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/export-to-rd`);
    };

    /**
     * Download file to export to RD
     * @param {String} uuid UUID of operation
     * @param {String} mimetype mime type
     * @param {String} filename Filename
     * @param {Object} config Axios config
     * @return {Object} Response data
     */
    downloadOperationsByUUId = (uuid, mimetype, filename = null, config = {}) => {
        Object.assign(config || {}, { headers: { Accept: mimetype }, responseType: 'arraybuffer' });
        return this.get(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}`, config).then(response =>
            download(response, filename)
        );
    };

    /**
     * Download RD file
     * @param {String} mimetype mime type
     * @param {String} filename Filename
     * @param {Object} config Axios config
     * @return {Object} Response data
     */
    downloadOperations = (mimetype, filename, config = {}) => {
        Object.assign(config || {}, { headers: { Accept: mimetype }, responseType: 'arraybuffer' });
        return this.get(`${ACCOUNTS_API_CONTEXT}/operations`, config).then(response => download(response, filename));
    };

    /**
     * Imports operations from RD
     * @return {Object} Response data
     */
    importRDOperations = () => {
        return this.post(`${ACCOUNTS_API_CONTEXT}/operations/export-to-rd-txt`, {}, { responseType: 'arraybuffer' });
    };

    createOperationsBulk = payload => {
        return this.post(`${ACCOUNTS_API_CONTEXT}/operations-bulk`, payload);
    };

    /**
     * Get operation Fiscal Items
     * @param {String} uuid operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation issues data
     */
    getOperationFiscalItems = (uuid, config) => {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${uuid}/fiscal-items`, config);
    };

    /**
     *
     * @param {String} operation_uuid UUID
     * @param {Object} document_uuid id Document
     * @return {Object} Promise
     */
    downloadOperationDocument = (operation_uuid, document_uuid) => {
        return this.get(`${ACCOUNTS_API_CONTEXT}/operations/${operation_uuid}/documents/${document_uuid}`, { responseType: 'blob' });
    };

    /**
     *
     * @return {Object} List with pay methods
     */
    getPaymentMethods = () => {
        return this.get(`${ACCOUNTS_API_CONTEXT}/payment-methods`);
    };

    /**
     * Get operation transfer details
     * @param {String} uuid operation UUID
     * @param {Object} config Axios config
     * @return {Object} Response with operation issues data
     */
    getOperationTransferDetails(id) {
        return this.getList(`${ACCOUNTS_API_CONTEXT}/operations/${id}/transfer-details`);
    }
}

const operationsService = new OperationsService();

export default operationsService;
